import React, { useEffect, useState } from 'react';
import toast from "react-hot-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAllLanguageFiles,
  getLanguageFileById,
  downloadLanguageFile,
  deleteLanguageFile
} from '../../../api/admin/languageManagement';
import { useTranslation } from '../../../hooks/useTranslation';

const ManageTab = ({platform}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  const { data: languageFilesData, isLoading: loading, isError, error } = useQuery({
    queryKey: ["languageFiles", platform], 
    queryFn: () => getAllLanguageFiles(platform),
    keepPreviousData: true,
  });

  const languageFiles = languageFilesData?.data || [];


   useEffect(() => {
    setSelectedFile(null);
    setPreviewContent(null);
  }, [platform]);

 const { data: fileContentData, isLoading: previewLoading } = useQuery({
    queryKey: ["languageFileContent", selectedFile?.id, platform],
    queryFn: () => getLanguageFileById(selectedFile.id, platform),
    enabled: !!selectedFile,
    onSuccess: (data) => setPreviewContent(data.data.content),
    onError: () => toast.error("Error Occured"),
  });

  const handlePreview = (file) => {
    setSelectedFile(file);
  };

  const { mutate: downloadMutation } = useMutation({
    mutationFn: downloadLanguageFile,
    onSuccess: (blobData, fileId) => {
      const file = languageFiles.find(f => f.id === fileId);
      if (file) {
        const url = window.URL.createObjectURL(blobData);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('file downloaded');
      }
    },
    onError: () => {
      toast.error('Error in downloading file');
    },
  });


  const handleDownload = (file) => {
    downloadMutation(file.id);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        <span className="ml-2">{t('languageManagement').manageTab.loading}</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-8">
        <div className="text-red-500 mb-2">{t('languageManagement').manageTab.error}</div>
        <p className="text-sm text-gray-400">{error?.message || t('languageManagement').manageTab.errorMessage}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {t('languageManagement').manageTab.title}
        </h3>
        <p className="text-gray-600">
          {t('languageManagement').manageTab.description}
        </p>
      </div>

      {/* Language Files List */}
      <div className="space-y-4">
        {languageFiles.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-500 mb-2">{t('languageManagement').manageTab.noFiles}</div>
            <p className="text-sm text-gray-400">{t('languageManagement').manageTab.noFilesDescription}</p>
          </div>
        ) : (
          languageFiles.map((file) => (
            <div key={file.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div className="text-lg font-bold text-gray-900">
                      {file.languageCode.toUpperCase()}
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        {file.languageCode.toUpperCase()} {file.language}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {t('languageManagement').manageTab.fileInfo.uploaded} • {file.fileSizeFormatted} • {formatDate(file.lastModified)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePreview(file)}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {t('languageManagement').manageTab.actions.preview}
                  </button>
                  <button
                    onClick={() => handleDownload(file)}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {t('languageManagement').manageTab.actions.download}
                  </button>

                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Preview Section */}
      {selectedFile && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-lg font-medium text-gray-900">
                {t('languageManagement.manageTab.preview.title', { language: selectedFile.language })}
              </h4>
              <p className="text-sm text-gray-500">
                {t('languageManagement').manageTab.preview.description}
              </p>
            </div>
            <button
              onClick={() => {
                setSelectedFile(null);
                setPreviewContent(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {previewLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
              <span className="ml-2">{t('languageManagement').manageTab.preview.loading}</span>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
              <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                {fileContentData?.data?.content ? JSON.stringify(fileContentData?.data?.content, null, 2) : t('languageManagement').manageTab.preview.noContent}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ManageTab;
